import { ETypeNotification } from "@/modules/helpers/enum"
import { Campaign, Notification, RequestProductSample } from "@/modules/types"

interface ParsedAdditionalData {
  campaign?: Campaign
  submission?: { id?: string }
  conversation?: {
    code?: string
    otherUserId?: string
    shopName?: string
  }
  deeplink?: string
  buttonText?: string
  showSkuIds?: string[]
  requestProductSamples?: RequestProductSample
}

interface NotificationData {
  deepLink?: string
  buttonText?: string
  campaignId?: string
  submissionId?: string
  conversationCode?: string
  otherUserId?: string
  shopName?: string
  campaign?: Campaign
  showSkuIds?: string[]
  requestProductSamples?: RequestProductSample
}

export interface TextToken {
  content: string
  type: "text" | "highlight" | "percentage"
}

// Notification type groups - using Sets for O(1) lookup performance
export const SUBMISSION_TYPES = new Set<ETypeNotification>([
  ETypeNotification.ACCEPT_SUBMISSION,
  ETypeNotification.REJECT_SUBMISSION,
  ETypeNotification.REMIND_SUBMISSION
])

export const DRAFT_TYPES = new Set<ETypeNotification>([
  ETypeNotification.APPROVE_DRAFT,
  ETypeNotification.REJECT_DRAFT,
  ETypeNotification.REVIEW_DRAFT,
  ETypeNotification.FINALIZE_DRAFT,
  ETypeNotification.COMMENT_DRAFT,
  ETypeNotification.REPLY_COMMENT
])

/**
 * Check if text contains HTML tags
 */
export const containsHTML = (text: string): boolean => {
  return /<[a-z][\s\S]*>/i.test(text)
}

/**
 * Parses notification text into tokens for highlighting
 */
export const parseNotificationTextToTokens = (
  text: string
): {
  description: string | TextToken[]
  isHTML: boolean
} => {
  const isHTML = containsHTML(text)

  if (isHTML) {
    return {
      description: text,
      isHTML: true
    }
  }

  const tokens: TextToken[] = []
  let lastIndex = 0

  // Match quoted strings (product names) and percentages
  const regex = /(".*?")|(\d+%)/g
  let match

  while ((match = regex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      tokens.push({
        content: text.substring(lastIndex, match.index),
        type: "text"
      })
    }

    // Add highlighted match
    if (match[1]) {
      // Quoted string - highlight in cyan
      tokens.push({
        content: match[1],
        type: "highlight"
      })
    } else if (match[2]) {
      // Percentage - highlight in red
      tokens.push({
        content: match[2],
        type: "percentage"
      })
    }

    lastIndex = match.index + match[0].length
  }

  // Add remaining text
  if (lastIndex < text.length) {
    tokens.push({
      content: text.substring(lastIndex),
      type: "text"
    })
  }

  return {
    description: tokens.length > 0 ? tokens : [{ content: text, type: "text" }],
    isHTML: false
  }
}

/**
 * Parses additional data from notification
 */
export const parseAdditionalData = (data: Notification): NotificationData => {
  const result: NotificationData = {}

  try {
    const rawAdditionalData = data.additionalData
    const parsed: ParsedAdditionalData =
      typeof rawAdditionalData === "string"
        ? JSON.parse(rawAdditionalData || "{}")
        : rawAdditionalData || {}

    // Extract deeplink / buttonText from parsed data or metadata
    const metaData =
      data.metaData && typeof data.metaData === "object"
        ? (data.metaData as { deeplink?: string; buttonText?: string })
        : undefined

    result.campaignId = parsed.campaign?.id
    result.deepLink = parsed.deeplink || metaData?.deeplink
    result.buttonText = parsed.buttonText || metaData?.buttonText
    result.submissionId = parsed.submission?.id
    result.conversationCode = parsed.conversation?.code
    result.otherUserId = parsed.conversation?.otherUserId
    result.shopName = parsed.conversation?.shopName
    result.campaign = parsed.campaign
    result.showSkuIds = parsed.showSkuIds
    result.requestProductSamples = parsed.requestProductSamples
  } catch (error) {
    console.warn("Failed to parse notification additional data:", error)
  }

  return result
}

/**
 * Gets the submissionId from notification if it's a submission or draft type
 * Returns null if not applicable
 */
export const getSubmissionIdFromNotification = (data: Notification): string | null => {
  const notificationType = data.type
  if (!notificationType) return null

  const { submissionId } = parseAdditionalData(data)

  // Check if this is a submission or draft type notification
  if (SUBMISSION_TYPES.has(notificationType) || DRAFT_TYPES.has(notificationType)) {
    return submissionId || null
  }

  return null
}
